/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import {
    MUENSTER_DEVICE_SENSOR_CONFIG,
    NEUTRAUBLING_DEVICE_SENSOR_CONFIG,
} from '@/config/deviceSensorConfig';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

/* Import types */
import type { DeviceSensorCsvMetadataGroup, DeviceSensorGroup } from '@/types/deviceSensor';

export type SensorDeviceCustomer = 'neutraubling' | 'muenster';
export type SensorGroupFilter = 'ALL' | 'NONE' | 'DTC' | DeviceSensorCsvMetadataGroup;
export type DeviceDefinitionGroupFilter = 'ALL' | DeviceSensorCsvMetadataGroup;

export interface DeviceDefinitionSystem {
    groups: Array<{
        devices: string[];
        group: DeviceSensorCsvMetadataGroup;
    }>;
    system: string;
}

export interface DemoSensorTarget {
    group: string;
    sensorName: string | null;
    sensorType: string;
}

const DEVICE_DEFINITION_METADATA_GROUPS: readonly DeviceSensorCsvMetadataGroup[] = [
    'BL',
    'HE',
    'PV',
    'WATER',
    'GAS',
    'GRID_FEED',
];

export function isDemoSensorMetadataGroupFilter(
    sensorGroupFilter: SensorGroupFilter,
): sensorGroupFilter is DeviceSensorCsvMetadataGroup {
    return DEVICE_DEFINITION_METADATA_GROUPS.includes(sensorGroupFilter as DeviceSensorCsvMetadataGroup);
}

function resolveCatalogGroupFilter(
    sensorGroupFilter: SensorGroupFilter,
): Exclude<DeviceSensorGroup, 'DTC' | null> | null {
    switch (sensorGroupFilter) {
        case 'PV':
        case 'BL':
        case 'HE':
            return sensorGroupFilter;
        case 'WATER':
            return 'WL';
        default:
            return null;
    }
}

function isVirtualDeviceEnabled(
    deviceSensorCatalog: DeviceSensorCatalog,
    deviceName: string,
    sensorGroupFilter: SensorGroupFilter,
): boolean {
    if (sensorGroupFilter === 'ALL') {
        return deviceSensorCatalog.getDeviceMetadataGroups(deviceName)
            .some((group) => deviceSensorCatalog.isMetadataGroupEnabled(deviceName, group));
    }

    if (isDemoSensorMetadataGroupFilter(sensorGroupFilter)) {
        return deviceSensorCatalog.isMetadataGroupEnabled(deviceName, sensorGroupFilter);
    }

    const filteredGroup = resolveCatalogGroupFilter(sensorGroupFilter);

    if (filteredGroup) {
        return deviceSensorCatalog.isGroupEnabled(deviceName, filteredGroup);
    }

    return deviceSensorCatalog.getSensorGroups(deviceName)
        .filter((group): group is Exclude<DeviceSensorGroup, 'DTC' | null> => group !== 'DTC')
        .some((group) => deviceSensorCatalog.isGroupEnabled(deviceName, group));
}

export function createDemoDeviceSensorCatalog(
    customer: SensorDeviceCustomer,
): DeviceSensorCatalog {
    return new DeviceSensorCatalog(
        customer === 'muenster'
            ? MUENSTER_DEVICE_SENSOR_CONFIG
            : NEUTRAUBLING_DEVICE_SENSOR_CONFIG,
    );
}

/** Returns the direct and calculated sensor targets displayed by the sensor/device demo. */
export function getDemoSensorTargets(
    deviceSensor: DeviceSensor,
    deviceSensorCatalog: DeviceSensorCatalog,
    sensorGroupFilter: SensorGroupFilter,
): DemoSensorTarget[] {
    const targets: DemoSensorTarget[] = [];
    const isMetadataGroupFilter = isDemoSensorMetadataGroupFilter(sensorGroupFilter);
    const shouldShowBaseSensor = sensorGroupFilter === 'ALL'
        || sensorGroupFilter === 'NONE'
        || isMetadataGroupFilter;
    const isMuensterDirectSensor = deviceSensor.getSensorName(null, null)?.startsWith('Mue.') ?? false;

    if (shouldShowBaseSensor) {
        if (isMuensterDirectSensor) {
            targets.push({
                group: '-',
                sensorName: deviceSensor.getSensorName(null, null),
                sensorType: '-',
            });
        }

        deviceSensor.getSensorTypes(null).forEach((sensorType) => {
            targets.push({
                group: '-',
                sensorName: deviceSensor.getSensorName(null, sensorType),
                sensorType,
            });
        });
    }

    deviceSensor.getSensorGroups().forEach((group) => {
        const metadataGroup = group === 'DTC'
            ? null
            : deviceSensorCatalog.getMetadataGroup(group as Exclude<DeviceSensorGroup, 'DTC' | null>);

        if (
            sensorGroupFilter !== 'ALL'
            && sensorGroupFilter !== group
            && sensorGroupFilter !== metadataGroup
        ) {
            return;
        }

        if (group === 'DTC') {
            targets.push({
                group,
                sensorName: deviceSensor.getSensorName('DTC', null),
                sensorType: '-',
            });
            return;
        }

        deviceSensorCatalog.getSensorTypes(group).forEach((sensorType) => {
            targets.push({
                group: metadataGroup ?? group,
                sensorName: deviceSensor.getSensorName(group as DeviceSensorGroup, sensorType),
                sensorType,
            });
        });
    });

    return targets;
}

export function getDemoVirtualDevices(
    deviceSensorCatalog: DeviceSensorCatalog,
    sensorGroupFilter: SensorGroupFilter,
    enabledOnly = true,
): string[] {
    const virtualDevices = isDemoSensorMetadataGroupFilter(sensorGroupFilter)
        ? deviceSensorCatalog.getVirtualDevicesByMetadataGroups([sensorGroupFilter])
        : sensorGroupFilter === 'ALL' || sensorGroupFilter === 'NONE'
            ? deviceSensorCatalog.getVirtualDevices()
            : deviceSensorCatalog.getVirtualDevices('DTC');

    return virtualDevices
        .filter((deviceName) => (
            !enabledOnly || isVirtualDeviceEnabled(
                deviceSensorCatalog,
                deviceName,
                sensorGroupFilter,
            )
        ))
        .sort((left, right) => left.localeCompare(right));
}

export function getDeviceDefinitionVirtualDevices(
    deviceSensorCatalog: DeviceSensorCatalog,
    sensorGroupFilter: DeviceDefinitionGroupFilter,
    enabledOnly = true,
): string[] {
    const groups = sensorGroupFilter === 'ALL'
        ? DEVICE_DEFINITION_METADATA_GROUPS
        : [sensorGroupFilter];

    return deviceSensorCatalog
        .getVirtualDevicesByMetadataGroups(groups)
        .filter((deviceName) => (
            !enabledOnly || groups.some((group) => (
                deviceSensorCatalog.isMetadataGroupEnabled(deviceName, group)
            ))
        ))
        .sort((left, right) => {
            if (!enabledOnly) {
                const leftEnabled = groups.some((group) => (
                    deviceSensorCatalog.isMetadataGroupEnabled(left, group)
                ));
                const rightEnabled = groups.some((group) => (
                    deviceSensorCatalog.isMetadataGroupEnabled(right, group)
                ));

                if (leftEnabled !== rightEnabled) {
                    return leftEnabled ? -1 : 1;
                }
            }

            return left.localeCompare(right);
        });
}

export function getDeviceDefinitionSystems(
    deviceSensorCatalog: DeviceSensorCatalog,
    enabledOnly = true,
): DeviceDefinitionSystem[] {
    const systems = new Map<string, Map<DeviceSensorCsvMetadataGroup, string[]>>();

    DEVICE_DEFINITION_METADATA_GROUPS.forEach((group) => {
        getDeviceDefinitionVirtualDevices(deviceSensorCatalog, group, enabledOnly)
            .forEach((deviceName) => {
                const system = deviceSensorCatalog.getDeviceSystem(deviceName) ?? deviceName;
                const groups = systems.get(system) ?? new Map<DeviceSensorCsvMetadataGroup, string[]>();
                const devices = groups.get(group) ?? [];

                devices.push(deviceName);
                groups.set(group, devices);
                systems.set(system, groups);
            });
    });

    return Array.from(systems.entries())
        .map(([system, groups]) => ({
            system,
            groups: Array.from(groups.entries()).map(([group, devices]) => ({
                group,
                devices: devices.sort((left, right) => left.localeCompare(right)),
            })),
        }))
        .sort((left, right) => left.system.localeCompare(right.system));
}
